import { Suspense, useEffect, useState } from 'react'
import { Outlet } from 'react-router'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { SxProps, Tab, Tabs } from '@mui/material'
import Box from '@mui/material/Box'
import { Loading } from 'components/ui'
import { useMappedTranslation } from 'hooks'
import { PRIVACY_TYPE, buildPolicySearch } from 'pages/policies/utils'

import i18n, { convertCountryCode2to3 } from '../../../../../utils/i18n'

type Props = {
  sx?: SxProps
}

function TabsLayout({ sx }: Props) {
  const location = useLocation()
  const navigate = useNavigate()
  const t = useMappedTranslation()
  const [searchParams] = useSearchParams()
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const policySearch = buildPolicySearch(searchParams)

  const isServices = () => {
    const currentRoute = location.pathname.toLowerCase()
    return currentRoute.includes('services')
  }

  const [activeTab, setActiveTab] = useState<PRIVACY_TYPE>(isServices() ? PRIVACY_TYPE.SERVICE : PRIVACY_TYPE.PRIVACY_NOTICE)

  useEffect(() => {
    setActiveTab(isServices() ? PRIVACY_TYPE.SERVICE : PRIVACY_TYPE.PRIVACY_NOTICE)
  }, [location])

  const isKorea = paramCountryCode ? convertCountryCode2to3(paramCountryCode) === 'KOR' : i18n.countryCode === 'KR'
  const tabStyle = {
    minWidth: { xs: '50%', md: '250px' },
    minHeight: '36px',
    height: '36px',
    p: '0px',
    fontSize: '12px',
    color: '#8C8C8C',
    fontWeight: 600,
    lineHeight: '22px',
    textTransform: 'none',
    '&.Mui-selected': {
      color: 'text.primary',
      lineHeight: '22px',
      fontWeight: 'bold',
      border: '1px solid #636363',
      borderRadius: '18px'
    },
    '&:focus-visible': {
      outlineOffset: '-2px',
      borderRadius: '18px !important'
    }
  }

  return (
    <Box sx={{ width: '100%', height: '100%', mt: !isKorea ? { xs: '-54px', md: '-12px' } : undefined }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: { xs: '0 24px', md: '0' }
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '1040px' }}>
          {isKorea && (
            <Tabs
              value={activeTab}
              onChange={(event, value) => setActiveTab(value)}
              centered
              TabIndicatorProps={{ sx: { display: 'none' } }}
              sx={{
                width: '100%',
                minHeight: '36px',
                height: '36px',
                marginBottom: '48px'
              }}
            >
              <Tab
                label={t('iam.02.Agree.Terms.PP.title.01')}
                value={PRIVACY_TYPE.PRIVACY_NOTICE}
                onClick={() => navigate(`/policies/privacy-notices/latest${policySearch}`)}
                sx={tabStyle}
                data-testid='TabAF84A323'
              />
              <Tab
                label={t('iam.membership.Common.Title.ServicePP')}
                value={PRIVACY_TYPE.SERVICE}
                onClick={() => navigate(`/policies/privacy-notices/services${policySearch}`)}
                sx={tabStyle}
                data-testid='Tab1329EEB9'
              />
            </Tabs>
          )}
          <Suspense fallback={<Loading fullScreen />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  )
}

export default TabsLayout
